import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { rootUrl } from "../../../config/config";

const parseSerials = (text) =>
    String(text || "")
        .split(/[\n,]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);

/**
 * Returns an error message when the serials don't match the quantity, or null
 */
export const serialCountError = (serials, quantity) => {
    if (!Number.isInteger(Number(quantity))) return "Serial-tracked parts can only be moved in whole units";
    const required = Math.abs(parseInt(quantity, 10) || 0);
    const list = serials || [];
    if (list.length !== required) {
        return `Enter exactly ${required} serial number(s), one per unit (got ${list.length})`;
    }
    if (new Set(list).size !== list.length) return "Each serial number can only be entered once";
    return null;
};

/**
 * Serial number capture for serial-tracked parts.
 * mode "enter": type new serials, one per line (receiving, positive adjustments, external job cards).
 * mode "select": pick serials currently in stock for partId (transfers, negative adjustments, issuing).
 */
export default function SerialNumbersField({ mode, partId, quantity, value = [], onChange, label = "Serial Numbers", extraOptions = [], allowFewer = false }) {
    const [text, setText] = useState(value.join("\n"));
    const [inStock, setInStock] = useState([]);
    const [loadingStock, setLoadingStock] = useState(false);
    const required = Math.abs(parseInt(quantity, 10) || 0);

    // Clear the textarea when the parent resets the form
    useEffect(() => {
        if (mode === "enter" && value.length === 0 && parseSerials(text).length > 0) setText("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, mode]);

    useEffect(() => {
        if (mode !== "select" || !partId) {
            setInStock([]);
            return;
        }
        const fetchInStock = async () => {
            setLoadingStock(true);
            try {
                const token = localStorage.getItem("authToken") || "default-token";
                const res = await axios.get(`${rootUrl}/api/parts/${encodeURIComponent(partId)}/serials`, {
                    params: { status: "IN_STOCK" },
                    headers: { Authorization: `Bearer ${token}` },
                });
                setInStock((res.data?.data || []).map((s) => s.serial_no));
            } catch (err) {
                console.error("Error fetching in-stock serials:", err);
                setInStock([]);
            } finally {
                setLoadingStock(false);
            }
        };
        fetchInStock();
    }, [mode, partId]);

    const count = value.length;
    const countClass = count === required
        ? "text-green-600"
        : allowFewer && count < required ? "text-amber-600" : "text-red-500";

    if (mode === "enter") {
        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}{allowFewer ? "" : " *"}</label>
                <textarea
                    rows={Math.min(Math.max(required, 2), 8)}
                    className="w-full border border-gray-300 rounded-lg p-2 font-mono text-sm uppercase"
                    placeholder="One serial number per line"
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        onChange(parseSerials(e.target.value));
                    }}
                />
                <p className={`text-xs mt-1 ${countClass}`}>{count} of {required} serial number(s) entered</p>
            </div>
        );
    }

    const optionValues = [...new Set([...extraOptions, ...inStock])].sort();
    const options = optionValues.map((s) => ({ value: s, label: s }));
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label} *</label>
            <Select
                isMulti
                options={options}
                value={value.map((s) => ({ value: s, label: s }))}
                onChange={(selected) => onChange((selected || []).map((o) => o.value))}
                isLoading={loadingStock}
                isDisabled={!partId}
                placeholder={partId ? "Select serial numbers in stock" : "Select a part first"}
                noOptionsMessage={() => "No serial numbers in stock for this part"}
                classNamePrefix="react-select"
            />
            <p className={`text-xs mt-1 ${countClass}`}>{count} of {required} serial number(s) selected</p>
        </div>
    );
}
